// Revert an animated box back to its original still image. Resolves the
// original url (stashed on the box by applyAnimationResult's forward branch
// at swap time — see originalImageUrl there; falls back to fetchFileVersions'
// referenceId chain for boxes animated before that stash existed), then
// applies the same box-swap-or-url-update decision + dispatch wiring the
// forward call sites already use (EditorShortcuts.jsx / EditorApp.jsx /
// JobWidgetHost.jsx). Shared by the Animator panel and the canvas "Revert to
// image" toolbar action so the resolve+gate+swap logic exists in exactly one
// place — see the EditorApp.jsx latent-bug note in applyAnimationResult.es6's
// history for what skipping the plugin-availability gate costs.
//
// The restored HotspotImages box gets the video box's current timeline range
// (applyAnimationResult's boxSwap decision now carries source.timeline onto
// newBox — same mechanism, same rule as the forward swap; no longer a fresh
// default timeline).
//
// Deliberately does NOT set window.bulkAnimateApplying — a revert is a normal
// user edit and stays undo-tracked (unlike bulk-animate apply).
import IdealStudio from "../../core/editor/main";
import { createBox } from "../../common/common_tools";
import { addBoxThunk, deleteBox, updatePluginToolbar } from "../../common/actions";
import { getDescendantBoxes } from "../../common/utils";
import { fetchFileVersions } from "../../common/server_utils";
import { applyAnimationResult } from "./applyAnimationResult.es6";

export function revertAnimatedBox(boxId) {
  return async (dispatch, getState) => {
    const present = getState().undoGroup.present;
    const srcTb = present.pluginToolbarsById?.[boxId];
    if (!srcTb) return { ok: false, reason: "not-found" };

    let originalUrl = srcTb.state?.originalImageUrl;
    if (!originalUrl && srcTb.state?.url) {
      const versions = await fetchFileVersions(srcTb.state.url);
      originalUrl = versions?.[0]?.fileVirtualPath || null;
    }
    if (!originalUrl) return { ok: false, reason: "not-found" };

    const decision = applyAnimationResult({
      boxId,
      url: originalUrl,
      boxes: present.boxesById,
      pluginToolbars: present.pluginToolbarsById,
      containedViewSelected: null,
      containedViews: present.containedViewsById,
      navItems: present.navItemsById,
      navItemSelected: present.boxesById?.[boxId]?.parent,
    });

    if (decision.kind === "boxSwap") {
      if (!IdealStudio.Plugins.get("HotspotImages")) {
        return { ok: false, reason: "plugin-missing" };
      }
      createBox(
        decision.newBox,
        "HotspotImages",
        decision.inASlide,
        (...args) => dispatch(addBoxThunk(...args)),
        present.boxesById
      );

      const oldBox = present.boxesById[decision.oldBoxId];
      const descendants = getDescendantBoxes(oldBox, present.boxesById);
      let cvs = [...(oldBox.containedViews || [])];
      descendants.forEach((descId) => {
        cvs = [...cvs, ...((present.boxesById[descId] || {}).containedViews || [])];
      });

      dispatch(
        deleteBox(
          decision.oldBoxId,
          decision.parent,
          decision.container,
          descendants,
          cvs,
          decision.page
        )
      );
    } else if (decision.kind === "urlUpdate") {
      dispatch(updatePluginToolbar(boxId, "main", "state", "url", originalUrl));
    }

    return { ok: true };
  };
}
